import React, { useState, useEffect, useCallback } from 'react';

function Settings({ token }) {
  const [settings, setSettings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState({});

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/settings', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch settings');
      const data = await response.json();
      setSettings(data);
      const values = {};
      data.forEach(s => { values[s.key] = s.value; });
      setEditValues(values);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const response = await fetch(`/api/settings/${key}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ value: editValues[key] })
      });
      if (!response.ok) throw new Error('Failed to update setting');
      const updated = await response.json();
      setSettings(prev => prev.map(s => s.key === key ? updated : s));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const getInputProps = (key) => {
    if (key === 'vector_chat_similarity_threshold') {
      return { type: 'number', step: '0.1', min: '0', max: '1' };
    }
    return { type: 'number', step: '1', min: '0' };
  };

  if (isLoading && settings.length === 0) return <div className="mt-4">Loading settings...</div>;

  return (
    <div className="card shadow-sm mt-4">
      <div className="card-header bg-primary text-white">
        <h5 className="mb-0">Settings</h5>
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}
        {settings.map(setting => (
          <div key={setting.key} className="border rounded p-3 mb-3">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <strong>{setting.key}</strong>
                {setting.description && <p className="text-muted mb-0 small">{setting.description}</p>}
              </div>
            </div>
            <div className="d-flex align-items-center">
              <input
                {...getInputProps(setting.key)}
                className="form-control form-control-sm me-2"
                style={{ maxWidth: '200px' }}
                value={editValues[setting.key] || ''}
                onChange={(e) => setEditValues(prev => ({ ...prev, [setting.key]: e.target.value }))}
              />
              <button
                className="btn btn-success btn-sm"
                onClick={() => handleSave(setting.key)}
                disabled={saving[setting.key] || editValues[setting.key] === setting.value}
              >
                {saving[setting.key] ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Settings;
